import React from "react";

import { HelpIcon } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import PageMeta from "Components/PageMeta";
import { AddButton } from "ds/Button";
import Loading from "ds/PageLoading";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { useLoadAccesses } from "Reducers/access/access.hooks";
import { environmentSelector } from "Reducers/environment";
import { projectCapabilitySelector } from "Reducers/project/capabilities";
import { gitProjectSelector } from "Reducers/project/project";
import { useLoadDomain } from "Reducers/project/settings/domains/hooks/useLoadDomain";
import { useLoadDomains } from "Reducers/project/settings/domains/hooks/useLoadDomains";
import { useLoadEnvironmentDomains } from "Reducers/project/settings/environmentDomains/hooks/useLoadEnvironmentDomains";
import { useAppSelector } from "Store/hooks";

import EnvironmentDomainsEmpty from "./EnvironmentDomainsEmpty/EnvironmentDomainsEmpty";
import EnvironmentSettingsLayout from "./EnvironmentDomainsLayout";
import EnvironmentDomainsList from "./EnvironmentDomainsList/EnvironmentDomainsList";
import * as S from "./styles";

const EnvironmentDomainsSettings = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId: string;
    organizationId: string;
    projectId: string;
  }>();

  const environment = useSelectorWithUrlParams(environmentSelector);
  const project = useSelectorWithUrlParams(gitProjectSelector);

  const { domains } = useLoadDomains(project);
  const { hasDomainPermission } = useLoadDomain(project);

  const hasDomains = !!domains?.length;

  const isProductionEnvironment = environment?.type === "production";

  const { environmentDomains, loading } = useLoadEnvironmentDomains(
    environment,
    project
  );

  const loadingAccesses = useLoadAccesses();

  const encodedEnvironmentId = encodeURIComponent(environmentId);

  const capabilites = useAppSelector(state =>
    projectCapabilitySelector(state, { projectId, organizationId })
  );

  const capabilityCustomDomains = capabilites?.custom_domains?.enabled ?? false;

  const addDomain = () => {
    navigate(
      isProductionEnvironment
        ? `/${organizationId}/${projectId}/-/settings/domains/-/new`
        : `/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/domains/-/add`
    );
  };

  return (
    <EnvironmentSettingsLayout>
      <PageMeta
        title={`Settings | ${environment?.title} | ${project?.title}`}
      />

      <S.HeaderWrapper>
        <S.EnvironmentTitle tag="h2" id="environment_domain-settings-heading">
          {environment?.title || intl.formatMessage({ id: "general" })}
        </S.EnvironmentTitle>

        {environmentDomains?.length !== 0 && (
          <AddButton
            analyticId="settings.domains.add.new.title"
            id="add-new-variable"
            disabled={!hasDomainPermission}
            onClick={addDomain}
          >
            {intl.formatMessage({
              id: "settings.domains.add.new.title"
            })}
          </AddButton>
        )}
      </S.HeaderWrapper>

      <S.Help>
        <HelpIcon />
        {intl.formatMessage(
          { id: "settings.domains.learn_more" },
          {
            link: chunks => (
              <S.Link
                analyticId="links.documentation.domain"
                href={intl.formatMessage({ id: "links.documentation.domain" })}
              >
                {chunks}
              </S.Link>
            )
          }
        )}
      </S.Help>

      {loading || loadingAccesses ? (
        <Loading />
      ) : isProductionEnvironment &&
        hasDomains &&
        domains?.length !== 0 &&
        domains ? (
        <EnvironmentDomainsList
          environmentDomains={domains}
          userHasPermissions={hasDomainPermission}
        />
      ) : hasDomains &&
        capabilityCustomDomains &&
        environmentDomains?.length !== 0 &&
        environmentDomains ? (
        <EnvironmentDomainsList
          environmentDomains={environmentDomains}
          userHasPermissions={hasDomainPermission}
        />
      ) : (
        <EnvironmentDomainsEmpty
          capabilityCustomDomains={capabilityCustomDomains}
          userHasPermissions={hasDomainPermission}
        />
      )}
    </EnvironmentSettingsLayout>
  );
};
export default EnvironmentDomainsSettings;
